import { useEffect, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import PrintLogoHeader from "@/components/PrintLogoHeader";
import api, { formatError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Printer, DollarSign } from "lucide-react";

function currency(n) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);
}

function ymNow() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function labelForMonth(ym) {
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleString([], { month: "long", year: "numeric" });
}

export default function PayrollPage() {
  const [month, setMonth] = useState(ymNow());
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/payroll/commissions?month=${month}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatError(e.response?.data?.detail)));
  }, [month]);

  return (
    <DashboardShell>
      <PrintLogoHeader docTitle="Advisor Commission Report" />

      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="font-mono-label text-muted-foreground">Payroll</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter mt-1">Commission Report</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Every closed RO tied to a Decline Drive coupon, grouped by the advisor who wrote it up. Commission rate lives in Settings — hand this to whoever runs payroll each month.
          </p>
        </div>
        <div className="flex gap-2 no-print">
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="border border-border rounded-md px-3 py-2 text-sm bg-background text-foreground font-medium"
            data-testid="payroll-month-input"
          />
          <Button variant="outline" onClick={() => window.print()} data-testid="print-payroll-btn">
            <Printer className="w-4 h-4 mr-2" strokeWidth={1.75} />
            Print
          </Button>
        </div>
      </div>

      {data && (
        <>
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            <Card className="p-5 border-border" data-testid="payroll-revenue">
              <div className="font-mono-label text-muted-foreground">Recovered This Month</div>
              <div className="font-display text-3xl font-bold tracking-tighter mt-2 text-emerald-600">
                {currency(data.total_revenue)}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {labelForMonth(data.month)}
              </div>
            </Card>
            <Card className="p-5 border-border" data-testid="payroll-commission">
              <div className="font-mono-label text-muted-foreground">Total Commissions</div>
              <div className="font-display text-3xl font-bold tracking-tighter mt-2 text-[hsl(var(--gold))]">
                {currency(data.total_commission)}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {data.commission_percent}% of recovered revenue
              </div>
            </Card>
            <Card className="p-5 border-border">
              <div className="font-mono-label text-muted-foreground">Rate</div>
              <div className="font-display text-3xl font-bold tracking-tighter mt-2">
                {data.commission_percent}%
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                Change in Settings → Advisor Commission
              </div>
            </Card>
          </div>

          <Card className="p-0 overflow-hidden border-border">
            <div className="p-5 border-b border-border">
              <div className="font-display text-lg font-bold tracking-tight">By Advisor</div>
              <div className="text-xs text-muted-foreground mt-1">
                Ranked by recovered revenue. Highest earners at top.
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Advisor</TableHead>
                  <TableHead className="text-right">Closed ROs</TableHead>
                  <TableHead className="text-right">Revenue Recovered</TableHead>
                  <TableHead className="text-right">Commission @ {data.commission_percent}%</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data.rows || []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-sm text-muted-foreground">
                      <DollarSign className="inline w-4 h-4 mr-2" strokeWidth={1.75} />
                      No closed ROs this month yet. Close appointments to earn commission.
                    </TableCell>
                  </TableRow>
                )}
                {(data.rows || []).map((r) => (
                  <TableRow key={r.advisor_id} data-testid={`payroll-row-${r.advisor_id}`}>
                    <TableCell>
                      <div className="w-3.5 h-3.5 rounded-full ring-2 ring-background shadow" style={{ backgroundColor: r.advisor_color }} />
                    </TableCell>
                    <TableCell className="font-medium">{r.advisor_name}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.closed_ros}</TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">{currency(r.revenue)}</TableCell>
                    <TableCell className="text-right tabular-nums font-bold text-[hsl(var(--gold))]">
                      {currency(r.commission)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          <div className="text-xs text-muted-foreground pt-4 max-w-3xl mt-4">
            Commission is calculated on gross recovered service revenue (closed ROs tied to Decline Drive coupons). Payable per your dealership's standard payroll cycle. Adjust the rate in Settings → Advisor Commission.
          </div>
        </>
      )}
    </DashboardShell>
  );
}
